import {
  formatMaltaFullTimeFeeLineDisplay,
  MALTA_FULL_TIME_FEE_LINE_LABELS,
  MALTA_FULL_TIME_FEE_LINE_ORDER,
  MALTA_FULL_TIME_NOTICE_ORDER,
  MALTA_FULL_TIME_NOTICE_TEXT,
  type MaltaFullTimeFeeLineKey,
  type MaltaFullTimeFeeVisibility,
  type MaltaFullTimeNoticeKey,
} from "@/src/constants/AdmissionOffice/Malta/maltaFullTimeAdmissionConfig";
import { resolveIntakeAcademicYearLabel } from "@/src/utils/admissionOffice/malta/resolveIntakeAcademicYear";

const FEE_LINES_FOR_FIRST_YEAR_TOTAL: MaltaFullTimeFeeLineKey[] = [
  "tuitionFee",
  "acommodation",
  "medicalInsurance",
  "residencePermit",
  "admissionFee",
  "otherExpenses",
];

export const parseMaltaMoneyValue = (rawValue?: string) => {
  if (!rawValue) return 0;
  const cleaned = rawValue.replace(/[^\d.,]/g, "").trim();
  if (!cleaned) return 0;
  const normalized = cleaned.replace(/\s+/g, "").replace(",", ".");
  const numeric = Number.parseFloat(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const formatMaltaMoneyValue = (value: number) => {
  if (!Number.isFinite(value)) return "";
  if (Math.abs(value % 1) < 0.001) {
    return Math.round(value)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }
  return value.toFixed(2).replace(".", ",");
};

const paragraph = (text: string) => `<p>${text}</p>`;
const strongParagraph = (text: string) => `<p><strong>${text}</strong></p>`;
const spacer = () => `<p>&nbsp;</p>`;

const buildFeeLine = (key: MaltaFullTimeFeeLineKey, numericValue: string) => {
  const label = MALTA_FULL_TIME_FEE_LINE_LABELS[key];
  const displayValue = formatMaltaFullTimeFeeLineDisplay(key, numericValue);
  if (!displayValue) return "";
  return strongParagraph(`${label}: ${displayValue}`);
};

const buildDocumentsSection = () => `
    ${paragraph("Student should bring the following documents:")}
    <ul>
    <li>Original Invitation letter.</li>
    <li>Complete first year payment slip.</li>
    <li>8 passport size photos.</li>
    <li>Original School Certificates (should be legalized by the embassy of Ukraine).</li>
    </ul>
    ${spacer()}
    ${paragraph("Note! Groups will be created upon sufficient number of students.")}
    ${paragraph(
      "Please make sure that all the above-mentioned documents are with you before leaving your country. Immigration authorities may request them at the border control. You should inform us about your arrival details in order to avoid any problems at the border and to ensure that you are met by our representative. Please feel free to contact us by phone or e-mail.",
    )}
`;

const buildClosingSection = () =>
  spacer() +
  paragraph("We shall be glad to see you as a student and looking forward to welcoming you in our University.");

export interface BuildMaltaFullTimeAdmissionBodyParams {
  intake?: string;
  feeValues: Record<MaltaFullTimeFeeLineKey, string | undefined>;
  feeVisibility: MaltaFullTimeFeeVisibility;
  showSecondYear: boolean;
  noticeVisibility: Record<MaltaFullTimeNoticeKey, boolean>;
}

export const calcMaltaFullTimeFirstYearTotal = (
  feeValues: Record<MaltaFullTimeFeeLineKey, string | undefined>,
  feeVisibility: MaltaFullTimeFeeVisibility,
) =>
  FEE_LINES_FOR_FIRST_YEAR_TOTAL.reduce((total, key) => {
    if (!feeVisibility[key]) return total;
    return total + parseMaltaMoneyValue(feeValues[key]);
  }, 0);

export const calcMaltaFullTimeSecondYearTotal = (
  feeValues: Pick<Record<MaltaFullTimeFeeLineKey, string | undefined>, "tuitionFee" | "acommodation">,
) => parseMaltaMoneyValue(feeValues.tuitionFee) + parseMaltaMoneyValue(feeValues.acommodation);

export const buildMaltaFullTimeAdmissionBody = ({
  intake,
  feeValues,
  feeVisibility,
  showSecondYear,
  noticeVisibility,
}: BuildMaltaFullTimeAdmissionBodyParams): string => {
  const academicYear = resolveIntakeAcademicYearLabel(intake);
  const parts: string[] = [];

  parts.push(
    paragraph(
      `This letter is to confirm the admission of above named student in the course mentioned above for the ${academicYear} academic year.`,
    ),
  );
  parts.push(spacer());

  MALTA_FULL_TIME_FEE_LINE_ORDER.forEach((key) => {
    if (!feeVisibility[key]) return;
    const value = feeValues[key]?.trim();
    if (!value) return;
    const feeLine = buildFeeLine(key, value);
    if (feeLine) {
      parts.push(feeLine);
    }
  });

  if (showSecondYear) {
    parts.push(strongParagraph("Second year:"));
    if (feeVisibility.tuitionFee && feeValues.tuitionFee?.trim()) {
      parts.push(buildFeeLine("tuitionFee", feeValues.tuitionFee));
    }
    if (feeVisibility.acommodation && feeValues.acommodation?.trim()) {
      parts.push(buildFeeLine("acommodation", feeValues.acommodation));
    }
    const secondYearTotal = formatMaltaMoneyValue(calcMaltaFullTimeSecondYearTotal(feeValues));
    if (secondYearTotal) {
      parts.push(strongParagraph(`Complete second year expenses and onwards: ${secondYearTotal} EUR every year`));
    }
  }

  parts.push(spacer());
  parts.push(buildDocumentsSection());
  parts.push(spacer());

  let hasPreviousNotice = false;
  MALTA_FULL_TIME_NOTICE_ORDER.forEach((key) => {
    if (!noticeVisibility[key]) return;
    if (hasPreviousNotice) {
      parts.push(spacer());
    }
    parts.push(paragraph(MALTA_FULL_TIME_NOTICE_TEXT[key]));
    hasPreviousNotice = true;
  });

  parts.push(buildClosingSection());

  return parts.join("\n");
};
